import { RefObject, useCallback, useEffect, useState } from 'react';

interface ScrollGradientState {
  showTopGradient: boolean;
  showBottomGradient: boolean;
}

// Reports whether a scroll container has hidden content above/below the
// viewport. Pair with edge-fade overlays to signal more content out of view.
export const useScrollGradient = (
  scrollRef: RefObject<HTMLElement>,
  resetDep?: unknown
): ScrollGradientState => {
  const [showTopGradient, setShowTopGradient] = useState(false);
  const [showBottomGradient, setShowBottomGradient] = useState(false);

  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const hasOverflow = el.scrollHeight > el.clientHeight;
    const atTop = el.scrollTop <= 0;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
    setShowTopGradient(hasOverflow && !atTop);
    setShowBottomGradient(hasOverflow && !atBottom);
  }, [scrollRef]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    el.addEventListener('scroll', onScroll, { passive: true });
    const ro = new ResizeObserver(onScroll);
    ro.observe(el);
    Array.from(el.children).forEach((child) => ro.observe(child));
    onScroll();
    return () => {
      el.removeEventListener('scroll', onScroll);
      ro.disconnect();
    };
  }, [onScroll, resetDep]);

  return { showTopGradient, showBottomGradient };
};
